import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { brandConfig } from '../brand';
import { Mail, Search, Shield, HelpCircle } from 'lucide-react';

const EmailHelpPage: React.FC = () => {
  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-primary-100">
            <HelpCircle className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="mt-3 text-3xl font-bold text-gray-900">Finding Your Login Email</h1>
          <p className="text-lg text-gray-600 mt-2">
            Here's what to look for in your inbox
          </p>
        </div>

        {/* Email details card */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-primary-50">
                <Mail className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">From</h2>
                <p className="text-gray-700 mt-1">
                  <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">noreply@serendipitytechnology.com</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  <a href="https://serendipitytechnology.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">Serendipity Technology</a> is our technology partner that manages the {brandConfig.name} membership portal.
                </p>
              </div>
            </div>

            <hr className="border-gray-200" />

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-primary-50">
                <Search className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Subject Line</h2>
                <p className="text-gray-700 mt-1">
                  <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">Your Login Link to NW Dems Profile</span>
                </p>
              </div>
            </div>

            <hr className="border-gray-200" />

            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-primary-50">
                <Shield className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Is this email safe?</h2>
                <p className="text-sm text-gray-700 mt-1">
                  Yes. This is a legitimate email from {brandConfig.name}. The login link uses secure, one-time authentication — no password is stored or transmitted.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Email preview */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Email Preview</h2>
          <p className="text-sm text-gray-500 mb-4">This is what the login email looks like:</p>

          {/* Scaled-down email preview */}
          <div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            {/* Inbox preview bar */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Mail className="h-3.5 w-3.5" />
                <span className="font-medium text-gray-700">noreply@serendipitytechnology.com</span>
              </div>
              <p className="text-sm font-medium text-gray-900 mt-0.5">Your Login Link to NW Dems Profile</p>
            </div>

            {/* Simplified visual preview of the branded email */}
            <div className="bg-[#f0f4f8] p-4 sm:p-6">
              <div className="max-w-sm mx-auto bg-white rounded-xl overflow-hidden shadow-sm">
                {/* Header with logo */}
                <div className="bg-white px-6 pt-6 pb-4 text-center">
                  <img
                    src="https://nwdemocrats.org/wp-content/uploads/2026/02/FullLogo_Transparent-scaled-e1772140390249.png"
                    alt="NW Democrats"
                    className="h-12 mx-auto"
                  />
                </div>

                {/* Gradient bar */}
                <div className="h-1" style={{ background: 'linear-gradient(to right, #174265, #2b89b3, #5ac1ee)' }} />

                {/* Body */}
                <div className="px-6 pt-6 pb-4 text-center">
                  <h3 className="text-lg font-bold" style={{ color: '#174265' }}>
                    Sign in to your account
                  </h3>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Click the button below to securely sign in to your {brandConfig.name} membership portal. No password needed.
                  </p>
                </div>

                {/* CTA Button */}
                <div className="px-6 pb-3 text-center">
                  <span
                    className="inline-block text-white text-xs font-semibold px-8 py-2.5 rounded-full"
                    style={{ background: 'linear-gradient(135deg, #5ac1ee, #38a3d1)' }}
                  >
                    Sign In
                  </span>
                  <p className="text-[10px] text-gray-400 mt-2">
                    This link expires in 24 hours and can only be used once.
                  </p>
                </div>

                {/* Divider + help */}
                <div className="mx-6 border-t border-gray-100" />
                <p className="text-[10px] text-gray-400 text-center px-6 py-3">
                  Didn't request this? You can safely ignore this email.
                </p>

                {/* Footer */}
                <div className="px-6 py-4 text-center" style={{ backgroundColor: '#174265' }}>
                  <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    &copy; 2026 NW Democrats
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting tips */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-8">
          <h2 className="text-sm font-semibold text-amber-900 mb-3">Can't find the email?</h2>
          <ul className="text-sm text-amber-800 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">1.</span>
              <span>Check your <strong>spam or junk folder</strong> — the email may have been filtered.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">2.</span>
              <span>Search your inbox for <strong>"serendipitytechnology.com"</strong> or <strong>"NW Dems Profile"</strong>.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">3.</span>
              <span>Make sure you entered the same email address you registered with.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">4.</span>
              <span>Wait a minute — the email can sometimes take up to 60 seconds to arrive.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">5.</span>
              <span>Still stuck? Contact us at{' '}
                <a href={`mailto:${brandConfig.contactEmail}`} className="text-amber-900 underline">
                  {brandConfig.contactEmail}
                </a>
              </span>
            </li>
          </ul>
        </div>

        {/* Back links */}
        <div className="text-center space-x-4">
          <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500">
            Back to Sign In
          </Link>
          <span className="text-gray-300">|</span>
          <Link to="/register" className="text-sm text-primary-600 hover:text-primary-500">
            Register
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default EmailHelpPage;
